import React from "react";
import { motion } from "framer-motion";
import { MessageCircle, ArrowDown, BadgeCheck, ShieldCheck, Award, Clock } from "lucide-react";
import { WHATSAPP_URL } from "../../lib/contact";
import heroPhoto from "../../assets/images/hero-arquitecto.jpg";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: "easeOut", delay: i * 0.08 },
  }),
};

const badges = [
  { icon: BadgeCheck,  label: "Valuador Certificado" },
  { icon: ShieldCheck, label: "Perito Oficial" },
  { icon: Award,       label: "DRO Autorizado" },
  { icon: Clock,       label: "+40 Años de Trayectoria" },
];

export default function Hero() {
  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative pt-32 pb-20 lg:pt-36 lg:pb-28 min-h-screen flex items-center bg-brand-light overflow-hidden"
    >
      {/* Subtle dot grid background */}
      <div
        aria-hidden
        className="absolute inset-0 bg-dots-grid bg-dots-24 opacity-70 pointer-events-none"
      />
      {/* Gold left vertical accent */}
      <div className="absolute left-0 top-1/4 h-1/2 w-px bg-gradient-to-b from-transparent via-brand-gold to-transparent opacity-60" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* LEFT — Copy */}
        <div className="lg:col-span-7">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={0}
            className="flex items-center gap-4"
          >
            <span className="h-px w-10 bg-brand-gold" />
            <span className="label-overline" data-testid="hero-overline">
              Puebla, México · Desde 1982
            </span>
          </motion.div>

          <motion.h1
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={1}
            className="mt-6 font-playfair text-4xl sm:text-5xl lg:text-6xl xl:text-[64px] leading-[1.05] tracking-tight text-brand-navy text-balance"
            data-testid="hero-heading"
          >
            Más de <span className="italic text-brand-gold">40 Años</span> de Experiencia en{" "}
            <span className="relative inline-block">
              Valuación
              <svg className="absolute -bottom-2 left-0 w-full" height="6" viewBox="0 0 200 6" preserveAspectRatio="none">
                <path d="M0,3 Q100,0 200,3" stroke="#C9A84C" strokeWidth="2" fill="none" />
              </svg>
            </span>{" "}
            y Dirección de Obra
          </motion.h1>

          <motion.p
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={2}
            className="mt-6 max-w-xl font-inter text-lg lg:text-xl text-brand-mute leading-relaxed"
            data-testid="hero-subheading"
          >
            Avalúos, peritajes y DRO con respaldo técnico, legal y profesional.
            Confianza, precisión y resultados en cada proyecto.
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={3}
            className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4"
          >
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-button"
              className="group relative inline-flex items-center justify-center gap-2.5 overflow-hidden rounded-sm bg-brand-gold px-7 py-4 font-mont text-xs font-semibold uppercase tracking-[0.16em] text-brand-navy shadow-[0_10px_30px_-12px_rgba(201,168,76,0.6)] hover:shadow-[0_18px_44px_-12px_rgba(201,168,76,0.7)] hover:-translate-y-0.5 transition-all duration-300"
            >
              <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent group-hover:translate-x-full transition-transform duration-700" />
              <MessageCircle size={18} strokeWidth={2} />
              Escríbenos por WhatsApp
            </a>

            <a
              href="#contacto"
              data-testid="hero-cotizacion-button"
              className="inline-flex items-center justify-center gap-2 rounded-sm border-2 border-brand-navy px-7 py-4 font-mont text-xs font-semibold uppercase tracking-[0.16em] text-brand-navy hover:bg-brand-navy hover:text-white transition-all duration-300"
            >
              Solicitar Cotización
              <ArrowDown size={16} strokeWidth={2} />
            </a>
          </motion.div>

          {/* Trust badges */}
          <motion.ul
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={4}
            className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3"
            data-testid="hero-trust-badges"
          >
            {badges.map(({ icon: Icon, label }) => (
              <li key={label} className="flex items-center gap-2">
                <Icon size={16} strokeWidth={2} className="text-brand-gold" />
                <span className="font-inter text-xs sm:text-sm font-medium text-brand-navy/80">{label}</span>
              </li>
            ))}
          </motion.ul>
        </div>

        {/* RIGHT — Client business card photo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: "easeOut", delay: 0.2 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative max-w-[520px] mx-auto">
            {/* Back gold card */}
            <div className="absolute -top-6 -right-4 w-full h-full bg-brand-gold/15 border border-brand-gold/40 rounded-sm" />
            {/* Back navy blueprint card */}
            <div className="absolute -bottom-5 -left-5 w-full h-full bg-brand-dark rounded-sm overflow-hidden">
              <div aria-hidden className="absolute inset-0 bg-blueprint-dark bg-grid-32 opacity-60" />
            </div>

            {/* Framed image */}
            <div className="relative bg-white border border-brand-navy/10 rounded-sm shadow-[0_30px_80px_-30px_rgba(15,28,48,0.5)] p-3 sm:p-4 z-[1]">
              <div className="absolute -top-3 left-6 bg-brand-gold text-brand-navy font-mont text-[10px] uppercase tracking-[0.22em] px-3 py-1 rounded-sm shadow">
                Perito Especializado
              </div>
              <img
                src={heroPhoto}
                alt="Rodríguez Valuación y DRO — Avalúos certificados, perito especializado, 2 Sur 4905 Col. Las Palmas, Puebla."
                className="w-full h-auto rounded-sm block"
                loading="eager"
                width="1280"
                height="720"
                data-testid="hero-image"
              />
              <div className="mt-3 flex items-center justify-between px-2 pb-1">
                <div className="flex items-center gap-2">
                  <span className="h-px w-8 bg-brand-gold" />
                  <span className="font-mont text-[10px] uppercase tracking-[0.22em] text-brand-mute">
                    Desde 1982
                  </span>
                </div>
                <span className="font-playfair italic text-brand-navy text-sm">
                  Puebla · México
                </span>
              </div>
            </div>

            {/* Floating credential cards — subtle, positioned to not obscure the card */}
            <div className="absolute -left-16 top-8 z-10 animate-float-slow hidden xl:block">
              <FloatingPill label="Valuador" sub="Certificado" />
            </div>
            <div className="absolute -right-16 -bottom-4 z-10 animate-float-medium hidden xl:block">
              <FloatingPill label="DRO" sub="Autorizado" highlight />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function FloatingPill({ label, sub, highlight = false }) {
  return (
    <div
      className={`backdrop-blur-md border rounded-sm px-3.5 py-2.5 shadow-[0_18px_40px_-18px_rgba(0,0,0,0.5)] ${
        highlight
          ? "bg-brand-gold/95 border-brand-gold text-brand-navy"
          : "bg-white/95 border-white/40 text-brand-navy"
      }`}
    >
      <div className={`font-mont text-[9px] uppercase tracking-[0.22em] ${highlight ? "text-brand-navy/70" : "text-brand-mute"}`}>
        {sub}
      </div>
      <div className="font-playfair text-base leading-tight">{label}</div>
    </div>
  );
}
